'use client';

import React from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import {
  FilterSelect,
  ModelSearchSelect,
  FilterOption,
  Checkbox,
  ResponsiveTooltip,
} from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import { SearchInputIc, ResetFiltersIc, AccountIcon } from '../../../../components/icons';
import { getPeriodOptions, getCampaignTypeOptions } from '../constants';
import { FiltersProps } from '../types';
import CampaignSearchSelect from './CampaignSearchSelect';

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  localSearch,
  availableModels,
  availableCampaigns = [],
  isLoading,
  isCampaignsLoading = false,
  hasActiveFilters,
  onSearchChange,
  onFilterChange,
  onResetFilters,
  onModelSearchChange,
}) => {
  const { t } = useTranslations();

  const modelOptions: FilterOption[] = availableModels.map((model) => ({
    key: model.id,
    avatar: model.avatar,
    username: model.username,
    name: model.name,
    label: `${model.name} (@${model.username})`,
  }));

  const checkboxFilters = [
    // { key: 'paidSubscription', label: t('partner.revshare.subscribers.filters.paidSubscription') },
    // { key: 'paidSubscriptionAutoRenew', label: t('partner.revshare.subscribers.filters.paidSubscriptionAutoRenew') },
    { key: 'onePurchase', label: t('partner.revshare.subscribers.filters.onePurchase') },
    { key: 'twoPurchases', label: t('partner.revshare.subscribers.filters.twoPurchases') },
    { key: 'fivePurchases', label: t('partner.revshare.subscribers.filters.fivePurchases') },
    { key: 'payments500', label: t('partner.revshare.subscribers.filters.payments500') },
    // { key: 'messages', label: t('partner.revshare.subscribers.filters.messages') },
  ];

  return (
    <div className="flex flex-col gap-2 w-full">
      {/* Row 1: Search and dropdown filters */}
      <div className="flex items-center gap-4 md:flex-wrap max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        <Input
          type="text"
          placeholder={t('partner.revshare.subscribers.searchPlaceholder')}
          size="sm"
          value={localSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: 'w-[17.0625rem]',
            base: 'w-[17.0625rem]',
            input: 'text-sm text-auth-ink placeholder:text-neutral-700',
            inputWrapper:
              'rounded-2 h-9 w-[17.0625rem] bg-auth-surface border-none shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
            innerWrapper: 'bg-transparent',
          }}
        />
        <ModelSearchSelect
          placeholder={t('partner.revshare.subscribers.filters.account')}
          options={modelOptions}
          selectedKey={filters.account}
          onSelectionChange={(value) => onFilterChange('account', value === 'all' ? '' : value)}
          onSearchChange={onModelSearchChange}
          isLoading={isLoading}
          startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
          variant="campaign"
          autoWidth={true}
          t={t}
        />
        <FilterSelect
          placeholder={t('partner.revshare.subscribers.filters.campaignType') || 'Тип кампании'}
          options={getCampaignTypeOptions(t)}
          selectedKey={filters.campaign}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('campaign', value === 'all' ? '' : value)}
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.66602 10.0013C1.66602 6.07293 1.66602 4.10875 2.8864 2.88836C4.10679 1.66797 6.07098 1.66797 9.99935 1.66797C13.9277 1.66797 15.8919 1.66797 17.1123 2.88836C18.3327 4.10875 18.3327 6.07293 18.3327 10.0013C18.3327 13.9297 18.3327 15.8939 17.1123 17.1142C15.8919 18.3346 13.9277 18.3346 9.99935 18.3346C6.07098 18.3346 4.10679 18.3346 2.8864 17.1142C1.66602 15.8939 1.66602 13.9297 1.66602 10.0013Z"
                stroke="#A8A8B0"
              />
              <path d="M5.83398 15V7.5" stroke="#A8A8B0" strokeLinecap="round" />
              <path d="M10 15V5" stroke="#A8A8B0" strokeLinecap="round" />
              <path d="M14.166 14.9987V10.832" stroke="#A8A8B0" strokeLinecap="round" />
            </svg>
          }
          autoWidth={true}
        />
        <CampaignSearchSelect
          placeholder={t('partner.revshare.subscribers.filters.campaign') || 'Кампания'}
          campaigns={availableCampaigns}
          selectedKey={filters.campaignId}
          onSelectionChange={(value) => onFilterChange('campaignId', value)}
          isLoading={isCampaignsLoading}
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.66602 10.0013C1.66602 6.07293 1.66602 4.10875 2.8864 2.88836C4.10679 1.66797 6.07098 1.66797 9.99935 1.66797C13.9277 1.66797 15.8919 1.66797 17.1123 2.88836C18.3327 4.10875 18.3327 6.07293 18.3327 10.0013C18.3327 13.9297 18.3327 15.8939 17.1123 17.1142C15.8919 18.3346 13.9277 18.3346 9.99935 18.3346C6.07098 18.3346 4.10679 18.3346 2.8864 17.1142C1.66602 15.8939 1.66602 13.9297 1.66602 10.0013Z"
                stroke="#A8A8B0"
              />
              <path d="M5.83398 15V7.5" stroke="#A8A8B0" strokeLinecap="round" />
              <path d="M10 15V5" stroke="#A8A8B0" strokeLinecap="round" />
              <path d="M14.166 14.9987V10.832" stroke="#A8A8B0" strokeLinecap="round" />
            </svg>
          }
        />
        {/* <FilterSelect
          placeholder={t('partner.revshare.subscribers.filters.period')}
          options={getPeriodOptions(t)}
          selectedKey={filters.period}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('period', value)}
          startContent={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 5V10L13.3333 11.6667M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66666 14.6024 1.66666 10C1.66666 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z" stroke="#A8A8B0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          autoWidth={true}
        /> */}
        {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
        {hasActiveFilters && (
          <Tooltip content={t('partner.revshare.offers.filters.resetTooltip')} placement="top">
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={onResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>

      {/* Row 2: Checkbox filters */}
      <div className="flex items-center gap-4 p-2 pb-6  flex-wrap">
        {checkboxFilters.map((filter) => (
          <Checkbox
            key={filter.key}
            isSelected={filters[filter.key as keyof typeof filters] as boolean}
            onChange={(checked) => onFilterChange(filter.key as keyof typeof filters, checked)}
            label={filter.label}
            color="secondary"
          />
        ))}
      </div>
    </div>
  );
};

export default FiltersSection;
